import { type FormEvent, useEffect, useState } from "react";
import { type Account, ApiError, type Device, type DeviceKind, type PaynowKey, type ThisDevice, api } from "./api";
import { SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import {
  Actions,
  Alert,
  Badge,
  Box,
  Empty,
  Field,
  IconAction,
  Loading,
  Modal,
  ModalActions,
  Table,
  Th,
  TwoLine,
  Working,
} from "./ui";

const KIND_LABELS: Record<DeviceKind, string> = {
  ipad: "iPad",
  laptop: "Laptop",
  desktop: "Desktop",
  server: "Server",
};

const KIND_ICONS: Record<DeviceKind, string> = {
  ipad: "ri-tablet-line",
  laptop: "ri-macbook-line",
  desktop: "ri-computer-line",
  server: "ri-server-line",
};

/**
 * Devices, for a master.
 *
 * Adding a device here decides what letter goes inside the reference numbers it writes,
 * which is why it sits with the other things only a master does. Setting one up is two
 * steps on purpose: add it here, then read the code out to whoever is holding the iPad.
 */
export function Devices(): React.ReactElement {
  const [devices, setDevices] = useState<Device[]>([]);
  const [nextCode, setNextCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [retiring, setRetiring] = useState<Device | undefined>();
  /** The device being renamed (D, 8 October 2026). */
  const [renaming, setRenaming] = useState<Device | undefined>();
  /** Switched-off devices keep their letters on the books but stay out of the way. */
  const [showOff, setShowOff] = useState(false);
  const [claimCode, setClaimCode] = useState<{ code: string; device: string } | undefined>();
  /** The company's own cash and bank accounts, which are what a till can be paid into. */
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [settingMoney, setSettingMoney] = useState<Device | undefined>();

  /** Delete one never used; the server says when it has been used and can only be switched off. */
  async function remove(device: Device): Promise<void> {
    if (!window.confirm(`Delete ${device.name} (${device.code})? Only a device never used can be deleted.`)) return;
    try {
      await api.deleteDevice(device.id);
      setError(undefined);
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The device could not be deleted");
    }
  }

  async function reload(): Promise<void> {
    try {
      const [result, books] = await Promise.all([api.devices(), api.accounts()]);
      setDevices(result.devices);
      setNextCode(result.nextFreeCode);
      setAccounts(
        books.accounts.filter(
          (account) => account.ownerKind === "company" && account.isActive && (account.kind === "cash" || account.kind === "bank"),
        ),
      );
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load devices");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function reissue(device: Device): Promise<void> {
    try {
      const result = await api.reissueClaim(device.id);
      setClaimCode({ code: result.claimCode, device: device.name });
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not generate a new setup code");
    }
  }

  /** The account a till is paid into, as its POS sees it: the one set, or the only one there is. */
  function moneyName(id: string | null, kind: "cash" | "bank"): React.ReactNode {
    const set = accounts.find((account) => account.id === id);
    if (set) return set.name;
    const choices = accounts.filter((account) => account.kind === kind);
    if (choices.length === 1) {
      return <span className="text-[#8c9097] dark:text-white/50">{choices[0]?.name} (only account)</span>;
    }
    return <span className="text-warning font-semibold">Not configured</span>;
  }

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle="Register devices and assign payment accounts. Each device code appears in every sale and buyback reference the device creates."
        actions={
          <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setAdding(true)}>
            <i className="ri-add-line" /> Register device
          </button>
        }
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-8 col-span-12">
          <Box
            title="Devices"
            count={devices.filter((device) => showOff || device.isActive).length}
            actions={
              devices.some((device) => !device.isActive) ? (
                <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={() => setShowOff(!showOff)}>
                  {showOff ? "Hide switched off" : `Show switched off (${devices.filter((device) => !device.isActive).length})`}
                </button>
              ) : undefined
            }
          >
            {devices.length === 0 ? (
              <Empty icon="ri-tablet-line">No devices have been registered.</Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>Device code</Th>
                    <Th>Device</Th>
                    <Th>Assigned payment accounts</Th>
                    <Th>Status</Th>
                    <Th end>Action</Th>
                  </>
                }
              >
                {devices.filter((device) => showOff || device.isActive).map((device) => (
                  <tr key={device.id} className="border-b border-defaultborder">
                    <td>
                      <span className="avatar avatar-sm !rounded-full !bg-primary/10 !text-primary font-semibold !mb-0">
                        {device.code}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center">
                        <i className={`${KIND_ICONS[device.kind]} text-[1.125rem] text-[#8c9097] me-2`} />
                        <TwoLine
                          main={device.name}
                          sub={`${KIND_LABELS[device.kind]} · ${
                            device.lastSeenAt ? `last used ${new Date(device.lastSeenAt).toLocaleDateString("en-SG")}` : "never used"
                          }`}
                        />
                      </div>
                    </td>
                    <td className="text-[0.75rem] leading-snug">
                      <div>
                        <span className="text-[#8c9097] dark:text-white/50">Cash </span>
                        {moneyName(device.cashAccountId, "cash")}
                      </div>
                      <div>
                        <span className="text-[#8c9097] dark:text-white/50">PayNow </span>
                        {moneyName(device.paynowAccountId, "bank")}
                      </div>
                    </td>
                    <td>
                      {!device.isActive ? (
                        <Badge tone="light">Deactivated</Badge>
                      ) : device.isRegistered ? (
                        <Badge tone="success">Registered</Badge>
                      ) : (
                        <Badge tone="warning">Awaiting setup</Badge>
                      )}
                    </td>
                    <td>
                      <Actions>
                        {device.isActive ? (
                          <>
                            <IconAction
                              icon="ri-bank-line"
                              label="Where its money goes"
                              tip="Configure payment accounts"
                              tone="primary"
                              onClick={() => setSettingMoney(device)}
                            />
                            <IconAction
                              icon="ri-key-2-line"
                              label="New set-up code"
                              tip="Generate new setup code"
                              tone="primary"
                              onClick={() => void reissue(device)}
                            />
                            <IconAction
                              icon="ri-edit-line"
                              label="Rename"
                              tip="Rename device"
                              tone="primary"
                              onClick={() => setRenaming(device)}
                            />
                            <IconAction
                              icon="ri-shut-down-line"
                              label="Switch off"
                              tip="Deactivate device"
                              tone="warning"
                              onClick={() => setRetiring(device)}
                            />
                            <IconAction
                              icon="ri-delete-bin-line"
                              label="Delete"
                              tip="Delete a device never used"
                              tone="danger"
                              onClick={() => void remove(device)}
                            />
                          </>
                        ) : null}
                      </Actions>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Box>
        </div>

        <div className="xl:col-span-4 col-span-12">
          <SetUpThisOne onDone={() => void reload()} />
          <PaynowAlerts />
          <Box title="About device codes">
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-3">
              Each device has a letter code that appears in every sale and buyback reference it creates. Two
              iPads working offline at an event can never produce the same reference, because each one only uses
              its own code.
            </p>
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">
              A device code is never reused, even after the device is deactivated, because earlier references
              still contain it. The next available code is{" "}
              <span className="font-semibold text-defaulttextcolor dark:text-white">{nextCode ?? "none, all 26 are in use"}</span>.
            </p>
          </Box>
        </div>
      </div>

      {claimCode ? (
        <Modal
          open
          title={`Setup code for ${claimCode.device}`}
          onClose={() => setClaimCode(undefined)}
          footer={
            <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setClaimCode(undefined)}>
              Done
            </button>
          }
        >
          <div className="text-center py-2">
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-3">One-time setup code</p>
            <p className="font-semibold text-[2rem] tracking-[0.3em] mb-3 text-primary">{claimCode.code}</p>
            <p className="text-[0.813rem] mb-0">
              On that device, open the system, select Complete device setup and enter this code. The code can be
              used once and expires after one hour.
            </p>
          </div>
        </Modal>
      ) : null}

      <AddDevice
        open={adding}
        suggestedCode={nextCode}
        onClose={() => setAdding(false)}
        onAdded={(code, name) => {
          setAdding(false);
          setClaimCode({ code, device: name });
          void reload();
        }}
      />

      {settingMoney ? (
        <DeviceMoney
          device={settingMoney}
          accounts={accounts}
          onClose={() => setSettingMoney(undefined)}
          onDone={() => {
            setSettingMoney(undefined);
            void reload();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameDevice
          device={renaming}
          onClose={() => setRenaming(undefined)}
          onDone={() => {
            setRenaming(undefined);
            void reload();
          }}
        />
      ) : null}

      {retiring ? (
        <RetireDevice
          device={retiring}
          onClose={() => setRetiring(undefined)}
          onDone={() => {
            setRetiring(undefined);
            void reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Setting up the machine you are sitting at, without signing out first.
 *
 * The code can also be typed on the sign in screen, which is where somebody who cannot
 * sign in has to do it. Daren asked where it went while he was already signed in on the
 * iPad, which is a fair question, so it is here as well.
 */
function SetUpThisOne({ onDone }: { readonly onDone: () => void }): React.ReactElement | null {
  const [device, setDevice] = useState<ThisDevice | null | undefined>();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    api
      .thisDevice()
      .then((result) => {
        if (live) setDevice(result.device);
      })
      .catch(() => {
        if (live) setDevice(null);
      });
  }, []);

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      setDevice((await api.claimDevice(code.trim())).device);
      setCode("");
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The setup code was not accepted");
    } finally {
      setBusy(false);
    }
  }

  if (device === undefined) return null;

  return (
    <Box title="Current device">
      {device ? (
        <p className="text-[0.813rem] mb-0">
          <span className="badge bg-primary/10 text-primary me-1">{device.code}</span>
          <span className="font-semibold">{device.name}</span>
          <span className="text-[#8c9097] dark:text-white/50"> is registered. No further action is needed.</span>
        </p>
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-3">
            This device has not been registered yet. Register it above, then enter the setup code here. The code
            can also be entered on the sign-in screen, under Device name.
          </p>
          <Field id="this-device-code" label="Setup code">
            <input
              id="this-device-code"
              type="text"
              className="form-control text-center !tracking-[0.3em] font-semibold"
              placeholder="XXXXXXXX"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              value={code}
              onChange={(changed) => setCode(changed.target.value.toUpperCase())}
            />
          </Field>
          <button type="submit" className="ti-btn ti-btn-primary-full !mb-0 mt-3" disabled={busy || code.trim() === ""}>
            {busy ? <Working label="Checking" /> : "Complete device setup"}
          </button>
        </form>
      )}
    </Box>
  );
}

function AddDevice({
  open,
  suggestedCode,
  onAdded,
  onClose,
}: {
  readonly open: boolean;
  readonly suggestedCode: string | null;
  readonly onAdded: (claimCode: string, deviceName: string) => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<DeviceKind>("ipad");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (name.trim() === "" || !suggestedCode) return;
    setError(undefined);
    setBusy(true);
    try {
      const result = await api.registerDevice(name.trim(), kind);
      setName("");
      onAdded(result.claimCode, result.device.name);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not register the device");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Register device"
      onClose={onClose}
      footer={
        <ModalActions
          busy={busy}
          disabled={name.trim() === "" || !suggestedCode}
          label="Register and generate setup code"
          onCancel={onClose}
          onSave={() => void submit()}
        />
      }
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field
          id="device-name"
          label="Device name"
          className="col-span-12"
          hint="A name staff will recognise when they pick up the device."
        >
          <input
            id="device-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="iPad 3"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field id="device-kind" label="Device type" className="sm:col-span-6 col-span-12">
          <SelectField<DeviceKind>
            id="device-kind"
            value={kind}
            options={[
              { value: "ipad", label: "iPad" },
              { value: "laptop", label: "Laptop" },
              { value: "desktop", label: "Desktop" },
            ]}
            onChange={setKind}
          />
        </Field>
        <Field id="device-letter" label="Device code" className="sm:col-span-6 col-span-12" hint="The next unused code letter.">
          <input id="device-letter" type="text" className="form-control" value={suggestedCode ?? "None available"} readOnly />
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

/**
 * Where a till's money goes, chosen from the desk.
 *
 * The iPad asks for this before its first sale. It can be set on the iPad too, but only
 * while an admin is signed in there, and at a booth the iPad is signed in as whoever is
 * selling, so the desk is where it is usually done.
 */
function DeviceMoney({
  device,
  accounts,
  onClose,
  onDone,
}: {
  readonly device: Device;
  readonly accounts: readonly Account[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const cashChoices = accounts.filter((account) => account.kind === "cash");
  const bankChoices = accounts.filter((account) => account.kind === "bank");
  const [cashId, setCashId] = useState(device.cashAccountId ?? (cashChoices.length === 1 ? (cashChoices[0]?.id ?? "") : ""));
  const [paynowId, setPaynowId] = useState(
    device.paynowAccountId ?? (bankChoices.length === 1 ? (bankChoices[0]?.id ?? "") : ""),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.setDeviceAccounts(device.id, cashId || null, paynowId || null);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The payment accounts could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Configure payment accounts - ${device.name}`}
      onClose={onClose}
      footer={<ModalActions busy={busy} label="Save" onCancel={onClose} onSave={() => void submit()} />}
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field id="device-money-cash" label="Cash account" className="sm:col-span-6 col-span-12">
          <SelectField
            id="device-money-cash"
            value={cashId}
            placeholder="Select account"
            options={cashChoices.map((account) => ({ value: account.id, label: account.name }))}
            onChange={setCashId}
          />
        </Field>
        <Field id="device-money-paynow" label="PayNow account" className="sm:col-span-6 col-span-12">
          <SelectField
            id="device-money-paynow"
            value={paynowId}
            placeholder="Select account"
            options={bankChoices.map((account) => ({ value: account.id, label: account.name }))}
            onChange={setPaynowId}
          />
        </Field>
        <p className="col-span-12 text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
          The salesperson can still select another account at checkout. On the iPad, select Refresh products
          beside the search to apply this immediately.
        </p>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

/** A device's name and kind; its letter stays, since it is inside every reference it wrote. */
function RenameDevice({
  device,
  onClose,
  onDone,
}: {
  readonly device: Device;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [name, setName] = useState(device.name);
  const [kind, setKind] = useState<DeviceKind>(device.kind);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await api.renameDevice(device.id, name.trim(), kind);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The device could not be renamed");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Rename device ${device.code}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? <Working label="Saving" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid grid-cols-12 gap-4">
        <Field id="rename-device" label="Device name" className="col-span-12" hint={`The letter ${device.code} stays: it is in every reference this device has written.`}>
          <input id="rename-device" type="text" maxLength={60} className="form-control" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field id="rename-kind" label="Device type" className="col-span-12">
          <SelectField<DeviceKind>
            id="rename-kind"
            value={kind}
            options={(Object.keys(KIND_LABELS) as DeviceKind[]).map((entry) => ({ value: entry, label: KIND_LABELS[entry] }))}
            onChange={setKind}
          />
        </Field>
      </div>
    </Modal>
  );
}

function RetireDevice({
  device,
  onClose,
  onDone,
}: {
  readonly device: Device;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      await api.retireDevice(device.id, reason);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not deactivate the device");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Deactivate device ${device.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Keep device active
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-danger-full"
            disabled={busy || !reason.trim()}
            onClick={() => void submit()}
          >
            {busy ? <Working label="Deactivating" /> : "Deactivate device"}
          </button>
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-[0.813rem] mb-4">
          The device is signed out immediately and cannot sign in again. Its device code, {device.code}, stays
          retired because earlier references still contain it.
        </p>
        <Field id="retire-reason" label="Reason for deactivation">
          <input
            id="retire-reason"
            type="text"
            className="form-control"
            value={reason}
            placeholder="Lost at the Suntec event"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}

/**
 * Where OHC's Gmail script posts PayNow payments, and the keys it posts with (Daren,
 * 1 October 2026). A key is shown once, when it is made, and only its hash is kept, so it
 * goes from this screen to the script and nowhere else. A script that is gone, or a key
 * somebody has seen, is stopped here at once.
 */
function PaynowAlerts(): React.ReactElement {
  const [keys, setKeys] = useState<PaynowKey[] | undefined>();
  const [label, setLabel] = useState("");
  const [made, setMade] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const address = `${window.location.origin}/api/paynow/alerts`;

  async function load(): Promise<void> {
    try {
      setKeys((await api.paynowKeys()).keys);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load access keys");
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function make(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.makePaynowKey(label.trim());
      setMade(result.key);
      setCopied(false);
      setLabel("");
      await load();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not generate an access key");
    } finally {
      setBusy(false);
    }
  }

  const when = (at: string): string => new Date(at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <Box title="PayNow payment alerts">
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-3">
        Send OCBC PayNow alerts to the POS so staff can match incoming payments to sales. OHC's Gmail
        integration posts each alert to the address below with an access key generated here, and the iPads show
        the payment as it arrives.
      </p>
      <label htmlFor="paynow-address" className="form-label">
        Payment alert address
      </label>
      <input id="paynow-address" type="text" className="form-control mb-4" readOnly value={address} />
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="flex gap-2 mb-3">
        <input
          id="paynow-key-label"
          type="text"
          className="form-control"
          value={label}
          placeholder="Access key description"
          onChange={(changed) => setLabel(changed.target.value)}
        />
        <button type="button" className="ti-btn ti-btn-primary-full !mb-0 shrink-0" disabled={busy || !label.trim()} onClick={() => void make()}>
          {busy ? <Working label="Generating" /> : "Generate access key"}
        </button>
      </div>
      {keys === undefined ? (
        <Loading />
      ) : keys.length === 0 ? (
        <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">No access keys have been created.</p>
      ) : (
        <ul className="list-none mb-0">
          {keys.map((key) => (
            <li key={key.id} className="flex items-center justify-between gap-3 py-2 border-t border-defaultborder">
              <TwoLine
                main={
                  <span className={key.revokedAt ? "text-[#8c9097] line-through" : ""}>
                    {key.label} <span className="text-[#8c9097] font-normal">…{key.endsWith}</span>
                  </span>
                }
                sub={`Created by ${key.createdBy}, ${when(key.createdAt)} · ${
                  key.revokedAt ? "revoked" : key.lastUsedAt ? `last used ${when(key.lastUsedAt)}` : "not used yet"
                }`}
              />
              {key.revokedAt ? null : (
                <IconAction
                  icon="ri-forbid-line"
                  label="Stop this key"
                  tip="Revoke access key"
                  tone="danger"
                  onClick={() => void api.revokePaynowKey(key.id).then(load)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {made ? (
        <Modal
          open
          title="New access key"
          onClose={() => setMade(undefined)}
          footer={
            <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setMade(undefined)}>
              I have added it to the integration
            </button>
          }
        >
          <p className="text-[0.813rem] mb-3">
            This key is displayed once. Add it to the integration yourself, or hand it to the responsible person in
            person, not in a group chat. If it is lost, revoke it and generate another.
          </p>
          <input id="paynow-new-key" type="text" className="form-control font-mono mb-3" readOnly value={made} />
          <button
            type="button"
            className="ti-btn ti-btn-light !mb-0"
            onClick={() => {
              void navigator.clipboard
                .writeText(made)
                .then(() => setCopied(true))
                .catch(() => (document.getElementById("paynow-new-key") as HTMLInputElement | null)?.select());
            }}
          >
            <i className="ri-file-copy-line" /> {copied ? "Copied" : "Copy"}
          </button>
        </Modal>
      ) : null}
    </Box>
  );
}
